import { useState } from "react";

export type SortKey = "latest" | "rating" | "hot";

interface FilterBarProps {
  regions: string[];
  years: number[];
  genres: string[];
  region: string;
  year: string;
  genre: string;
  sort: SortKey;
  onRegionChange: (value: string) => void;
  onYearChange: (value: string) => void;
  onGenreChange: (value: string) => void;
  onSortChange: (value: SortKey) => void;
  onReset: () => void;
}

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新发布" },
  { key: "rating", label: "评分高低" },
  { key: "hot", label: "热门点击" },
];

function FilterRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-background-300/40 last:border-b-0">
      <span className="shrink-0 text-xs md:text-sm font-medium text-foreground-500 sm:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`px-3 py-1 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                active
                  ? "bg-primary-500 text-background-50 font-medium"
                  : "bg-background-200/70 text-foreground-700 hover:text-foreground-950 hover:bg-background-300"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function FilterBar({
  regions,
  years,
  genres,
  region,
  year,
  genre,
  sort,
  onRegionChange,
  onYearChange,
  onGenreChange,
  onSortChange,
  onReset,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const regionOptions = [
    { value: "all", label: "全部" },
    ...regions.map((r) => ({ value: r, label: r })),
  ];
  const yearOptions = [
    { value: "all", label: "全部" },
    ...years.map((y) => ({ value: String(y), label: String(y) })),
  ];
  const genreOptions = [
    { value: "all", label: "全部" },
    ...genres.map((g) => ({ value: g, label: g })),
  ];

  return (
    <div className="rounded-lg bg-background-100 border border-background-300/50">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 border-b border-background-300/40">
        <div className="flex items-center gap-2">
          <i className="ri-filter-3-line text-primary-500 text-lg" />
          <span className="font-heading text-sm font-semibold text-foreground-900">
            筛选与排序
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs text-foreground-600 hover:text-primary-500 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line" />
            重置
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="sm:hidden flex items-center gap-1 px-3 py-1.5 rounded-md text-xs bg-background-200/70 text-foreground-800 cursor-pointer whitespace-nowrap"
          >
            {open ? "收起" : "展开"}
            <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
        </div>
      </div>

      <div className={`px-4 md:px-5 pb-3 ${open ? "block" : "hidden sm:block"}`}>
        <FilterRow label="地区" options={regionOptions} value={region} onChange={onRegionChange} />
        <FilterRow label="年份" options={yearOptions} value={year} onChange={onYearChange} />
        <FilterRow label="类型" options={genreOptions} value={genre} onChange={onGenreChange} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pt-3">
          <span className="shrink-0 text-xs md:text-sm font-medium text-foreground-500 sm:w-16">
            排序
          </span>
          <div className="flex flex-wrap gap-2">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onSortChange(opt.key)}
                className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs md:text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sort === opt.key
                    ? "bg-secondary-500 text-background-50 font-medium"
                    : "bg-background-200/70 text-foreground-700 hover:text-foreground-950 hover:bg-background-300"
                }`}
              >
                <i
                  className={
                    opt.key === "latest"
                      ? "ri-time-line"
                      : opt.key === "rating"
                      ? "ri-star-line"
                      : "ri-fire-line"
                  }
                />
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}